"use client"

import { useEffect, useState } from "react"
import { Plus } from "lucide-react"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select"
import { PlacementDialog } from "./placement-dialog"
import { PlacementSummaryList } from "./placement-summary-list"
import { useGetCandidatesCandidateIdJobs, useGetPlacements } from "openapi/api/endpoints"

interface CandidatePlacementTabProps {
  candidateId: string
}

export function CandidatePlacementTab({ candidateId }: CandidatePlacementTabProps) {
  const [showPlacementDialog, setShowPlacementDialog] = useState(false)
  const [selectedPlacementJobId, setSelectedPlacementJobId] = useState("")

  const { data: placementsResponse } = useGetPlacements()
  const { data: candidateJobs } = useGetCandidatesCandidateIdJobs(candidateId, {
    query: { enabled: !!candidateId },
  })

  const candidatePlacements = (placementsResponse?.placements ?? []).filter((placement) => {
    const placementCandidateId = (placement as { candidate_id?: string | null }).candidate_id
    return placementCandidateId === candidateId
  })

  const selectedPlacementJob = candidateJobs?.find((job) => job.id === selectedPlacementJobId) ?? candidateJobs?.[0]

  useEffect(() => {
    if (!candidateJobs?.length) {
      setSelectedPlacementJobId("")
      return
    }

    const hasSelectedJob = candidateJobs.some((job) => job.id === selectedPlacementJobId)
    if (!hasSelectedJob) {
      setSelectedPlacementJobId(candidateJobs[0].id)
    }
  }, [candidateJobs, selectedPlacementJobId])

  return (
    <>
      <PlacementSummaryList
        placements={candidatePlacements}
        emptyTitle="Placements"
        emptyDescription="No placements found for this candidate."
        action={
          <div className="flex flex-col gap-2 sm:flex-row">
            <Select
              value={selectedPlacementJobId}
              onValueChange={setSelectedPlacementJobId}
              disabled={!candidateJobs?.length}
            >
              <SelectTrigger className="h-8 w-full text-[12px] sm:w-[220px]">
                <SelectValue placeholder="Select a job" />
              </SelectTrigger>
              <SelectContent>
                {candidateJobs?.map((job) => (
                  <SelectItem key={job.id} value={job.id}>
                    {job.title} {job.company_name ? `• ${job.company_name}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Same secondary treatment as "New event" on the Events tab. */}
            <button
              type="button"
              onClick={() => setShowPlacementDialog(true)}
              disabled={!selectedPlacementJob}
              className="inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-md border border-primary/35 bg-primary/[0.07] px-3 text-[12px] font-medium text-brand-ink transition-colors hover:bg-primary/[0.12] disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Plus className="h-3.5 w-3.5" />
              New placement
            </button>
          </div>
        }
      />

      {selectedPlacementJob && (
        <PlacementDialog
          isOpen={showPlacementDialog}
          onClose={() => setShowPlacementDialog(false)}
          jobTitle={selectedPlacementJob.title ?? ""}
          companyName={selectedPlacementJob.company_name ?? ""}
          candidateId={candidateId}
          jobId={selectedPlacementJob.id}
          jobCandidateId={selectedPlacementJob.job_candidate_id}
          creationDate={new Date().toISOString()}
        />
      )}
    </>
  )
}
